import { ChevronDownIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { VoiceType } from '../../../api/voice';
import { useBooleanState } from '../../../hooks/index/use-boolean-state';
import Segment from '../../shared/segment';
import { VoicePlayer } from '../../shared/voice-player';

import 'swiper/css';
import 'swiper/css/navigation';

const PANEL_BASE_CLASSES = `fixed bottom-0 left-1/2 z-[100] -translate-x-1/2
 sm:bottom-2 w-full sm:w-[650px] md:w-[750px] lg:w-[850px]
 rounded-none border-t border-sky-300 bg-gradient-to-br from-sky-100 via-sky-50
 to-sky-100 backdrop-blur-xl sm:rounded-[1.5rem] sm:border-2 sm:p-2 p-1
 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]`;

const FAB_BASE_CLASSES = `fixed bottom-4 right-4 z-[100] flex h-14 w-14 cursor-pointer
 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-sky-700
 text-white shadow-xl shadow-sky-500/40 hover:scale-105
 hover:from-sky-600 hover:to-sky-800 active:scale-95 sm:bottom-6 sm:right-6
 transition-[opacity,transform] duration-300`;

// #0284c7 is tailwind's sky-600 — swiper needs a raw CSS value here.
const SWIPER_STYLE = {
  '--swiper-navigation-size': '18px',
  '--swiper-navigation-color': '#0284c7',
  '--swiper-navigation-top-offset': '50%'
} as React.CSSProperties;

export type VoicesPropsType = { voices: VoiceType[] };

function Voices({ voices }: VoicesPropsType) {
  const { value: visible, toggle: toggleVisible } = useBooleanState(true);

  if (voices.length === 0) return null;

  return (
    <>
      <Segment
        noPad
        noMargin
        className={classNames(
          PANEL_BASE_CLASSES,
          visible
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-4 opacity-0 sm:translate-y-6'
        )}
        aria-hidden={!visible}
      >
        <div className='relative flex w-full flex-col'>
          <div className='mt-[-5px] flex items-center justify-between border-sky-200/50 px-2'>
            <button
              type='button'
              onClick={toggleVisible}
              aria-label='بستن پخش‌کننده صوتی'
              tabIndex={visible ? 0 : -1}
              className='rounded-full p-1 text-zinc-500 transition-colors
               hover:bg-gray-100 hover:text-zinc-800 active:scale-90 sm:p-1'
            >
              <ChevronDownIcon className='h-5 w-5 sm:h-[18px] sm:w-[18px]' />
            </button>
          </div>

          <div className='w-full'>
            {voices.length === 1 ? (
              <div className='px-2 pb-1'>
                <VoicePlayer voice={voices[0]} />
              </div>
            ) : (
              <Swiper
                // @ts-ignore
                slidesPerView={1}
                spaceBetween={35}
                navigation
                modules={[Navigation]}
                className='w-full px-6 pb-1 sm:px-10'
                style={SWIPER_STYLE}
              >
                {voices.map(voice => (
                  <SwiperSlide key={voice.id} className='w-full'>
                    <VoicePlayer voice={voice} />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
        </div>
      </Segment>

      <Segment
        noPad
        noMargin
        className={classNames(
          FAB_BASE_CLASSES,
          visible ? 'pointer-events-none scale-75 opacity-0' : 'scale-100 opacity-100'
        )}
        aria-hidden={visible}
      >
        <button
          type='button'
          onClick={toggleVisible}
          aria-label='باز کردن پخش‌کننده صوتی'
          tabIndex={visible ? -1 : 0}
          className='flex h-full w-full items-center justify-center'
        >
          <MusicalNoteIcon className='h-6 w-6 animate-pulse' />
        </button>
      </Segment>
    </>
  );
}

export default Voices;
